import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { assetClassesApi, assetClassTemplatesApi } from '../api/smartTokenizerApi';
import {
  mapAssetClassToFrontend,
  mapAssetClassTemplateDtoToFrontend,
  toAssetClassRequest,
  toAssetClassUpdateRequest
} from '../api/smartTokenizerMappers';
import { normalizePreparedTransactionResponse } from '../blockchain/offlineSigning';

function mapTokenizerContractDeployResponse(data) {
  if (data == null || typeof data !== 'object') {
    return { assetClass: null, deploymentTransactionHash: '' };
  }
  const deploymentTransactionHash = String(
    data.deploymentTransactionHash ?? data.DeploymentTransactionHash ?? ''
  ).trim();
  const raw = data.assetClass ?? data.AssetClass;
  const assetClass = mapAssetClassToFrontend(raw ?? data);
  return { assetClass, deploymentTransactionHash };
}

const AssetClassContext = createContext();

// Asset Class represents a standardized definition of an asset type
// Multiple assets can belong to the same class and share the same smart contract
export const ASSET_CLASS_TYPES = {
  FUNGIBLE_COMMODITY: {
    id: 'fungibleCommodity',
    name: 'Fungible Commodity',
    description: 'Standardized commodities tradable in spot markets',
    tokenStandard: 'CBC20',
    batchable: true,
    tradingMode: 'spot'
  },
  UNIQUE_COLLECTIBLE: {
    id: 'uniqueCollectible',
    name: 'Unique Collectible',
    description: 'One-of-a-kind items with individual characteristics',
    tokenStandard: 'CBC721',
    batchable: false,
    tradingMode: 'individual'
  },
  STANDARDIZED_SERIES: {
    id: 'standardizedSeries',
    name: 'Standardized Series',
    description: 'Series of similar items with consistent specifications',
    tokenStandard: 'CBC721',
    batchable: true,
    tradingMode: 'class'
  },
  FRACTIONAL_ASSET: {
    id: 'fractionalAsset',
    name: 'Fractional Asset',
    description: 'High-value assets split into tradeable fractions',
    tokenStandard: 'CBC1155',
    batchable: false,
    tradingMode: 'fractional'
  }
};

// Predefined asset class templates for common RWA types
export const ASSET_CLASS_TEMPLATES = {
  // Precious Metals
  GOLD_BAR_LBMA: {
    id: 'goldBarLBMA',
    name: 'LBMA Gold Bar',
    category: 'preciousMetals',
    classType: 'fungibleCommodity',
    standardSchema: {
      required: ['purity', 'weight', 'mint', 'serialNumber', 'assayNumber'],
      specifications: {
        purity: { type: 'number', min: 995, max: 999.9, unit: 'fineness' },
        weight: { type: 'number', min: 350, max: 430, unit: 'oz' },
        mint: { type: 'string', enum: ['PAMP', 'Valcambi', 'Heraeus', 'Argor'] },
        serialNumber: { type: 'string', pattern: '^[A-Z0-9]{6,12}$' },
        assayNumber: { type: 'string', pattern: '^[0-9]{6,10}$' }
      }
    },
    tradingUnit: 'oz',
    marketSymbol: 'GOLD'
  },
  
  SILVER_BAR_1000OZ: {
    id: 'silverBar1000oz',
    name: '1000oz Silver Bar',
    category: 'preciousMetals',
    classType: 'fungibleCommodity',
    standardSchema: {
      required: ['purity', 'weight', 'mint', 'serialNumber'],
      specifications: {
        purity: { type: 'number', min: 999, max: 999.9, unit: 'fineness' },
        weight: { type: 'number', min: 990, max: 1010, unit: 'oz' },
        mint: { type: 'string', enum: ['Johnson Matthey', 'Engelhard', 'Asahi'] },
        serialNumber: { type: 'string', pattern: '^[A-Z0-9]{6,12}$' }
      }
    },
    tradingUnit: 'oz',
    marketSymbol: 'SILVER'
  },

  // Real Estate
  APARTMENT_UNIT_CLASS_A: {
    id: 'apartmentUnitClassA',
    name: 'Class A Apartment Unit',
    category: 'realEstate',
    classType: 'standardizedSeries',
    standardSchema: {
      required: ['squareFootage', 'bedrooms', 'bathrooms', 'floor', 'building', 'unitNumber'],
      specifications: {
        squareFootage: { type: 'number', min: 500, max: 2000, unit: 'sqft' },
        bedrooms: { type: 'integer', min: 1, max: 4 },
        bathrooms: { type: 'number', min: 1, max: 3, step: 0.5 },
        floor: { type: 'integer', min: 1, max: 50 },
        building: { type: 'string', maxLength: 50 },
        unitNumber: {
          type: 'string',
          pattern: '^[A-Z0-9]{1,10}$',
          patternDescription:
            '1–10 characters: letters (A–Z) and/or digits (e.g. 1, 12, 4B). Lowercase letters are accepted and normalized.'
        }
      }
    },
    tradingUnit: 'unit',
    marketSymbol: 'APT-A'
  },

  // Agriculture
  COFFEE_ARABICA_PREMIUM: {
    id: 'coffeeArabicaPremium',
    name: 'Premium Arabica Coffee',
    category: 'agriculture',
    classType: 'fungibleCommodity',
    standardSchema: {
      required: ['grade', 'weight', 'origin', 'harvestDate', 'certification', 'moisture'],
      specifications: {
        grade: { type: 'string', enum: ['AA', 'A', 'AB', 'B'] },
        weight: { type: 'number', min: 50, max: 70, unit: 'kg' },
        origin: { type: 'string', maxLength: 100 },
        harvestDate: { type: 'date', maxAge: 365 },
        certification: { type: 'array', items: ['Organic', 'Fairtrade', 'Rainforest'] },
        moisture: { type: 'number', min: 10, max: 12, unit: 'percent' }
      }
    },
    tradingUnit: 'kg',
    marketSymbol: 'COFFEE'
  },

  // Art Collections
  DIGITAL_ART_COLLECTION: {
    id: 'digitalArtCollection',
    name: 'Digital Art Collection',
    category: 'artCreative',
    classType: 'standardizedSeries',
    standardSchema: {
      required: ['artist', 'collectionName', 'editionNumber', 'totalEditions', 'medium', 'dimensions'],
      specifications: {
        artist: { type: 'string', maxLength: 100 },
        collectionName: { type: 'string', maxLength: 100 },
        editionNumber: { type: 'integer', min: 1 },
        totalEditions: { type: 'integer', min: 1, max: 10000 },
        medium: { type: 'string', enum: ['Digital', 'NFT', 'Print', 'Mixed'] },
        dimensions: { type: 'string', pattern: '^\\d+x\\d+$' }
      }
    },
    tradingUnit: 'piece',
    marketSymbol: 'ART'
  },

  // Vehicle Asset Classes for CIP-227 Composable Vehicle NFTs
  MCLAREN_SUPERCAR: {
    id: 'mclarenSupercar',
    name: 'McLaren Supercar Collection',
    category: 'automotive',
    classType: 'uniqueCollectible',
    composability: {
      enabled: true,
      standard: 'CIP-227',
      supportedParts: ['engine', 'aerodynamics', 'wheels', 'interior', 'electronics', 'bodywork']
    },
    standardSchema: {
      required: ['vehicleName', 'model', 'year', 'vin', 'engineNumber', 'horsepower', 'marketValue', 'condition'],
      specifications: {
        vehicleName: { type: 'string', maxLength: 200 },
        manufacturer: { type: 'string', default: 'McLaren' },
        model: { type: 'string', maxLength: 100 },
        year: { type: 'integer', min: 1980, max: 2030 },
        vin: { type: 'string', pattern: '^[A-HJ-NPR-Z0-9]{17}$' },
        engineNumber: { type: 'string', maxLength: 50 },
        engineType: { type: 'string', maxLength: 100 },
        horsepower: { type: 'integer', min: 100, max: 2000 },
        torque: { type: 'integer', min: 100, max: 1000 },
        topSpeed: { type: 'integer', min: 100, max: 300 },
        acceleration: { type: 'number', min: 1, max: 10 },
        transmission: { type: 'string', enum: ['7-Speed DCT', '8-Speed DCT', 'Manual', 'Automatic'] },
        driveType: { type: 'string', enum: ['RWD', 'AWD', '4WD'] },
        fuelType: { type: 'string', enum: ['Gasoline', 'Hybrid', 'Electric', 'E85'] },
        marketValue: { type: 'number', min: 10000 },
        condition: { type: 'string', enum: ['NEW', 'EXCELLENT', 'GOOD', 'FAIR', 'POOR'] },
        mileage: { type: 'integer', min: 0 },
        currentOwner: { type: 'string', maxLength: 200 },
        location: { type: 'string', maxLength: 200 },
        insuranceValue: { type: 'number', min: 0 }
      }
    },
    tradingUnit: 'vehicle',
    marketSymbol: 'MCLR'
  },

  VEHICLE_ENGINE_PARTS: {
    id: 'vehicleEngineParts',
    name: 'Vehicle Engine Components',
    category: 'automotive',
    classType: 'fungibleCommodity',
    composability: {
      enabled: true,
      standard: 'CIP-227',
      partCategory: 'engine',
      attachable: true,
      detachable: true,
      critical: true
    },
    standardSchema: {
      required: ['partName', 'partNumber', 'serialNumber', 'manufacturer', 'condition', 'vehicleCompatibility'],
      specifications: {
        partName: { type: 'string', maxLength: 200 },
        partNumber: { type: 'string', maxLength: 100 },
        serialNumber: { type: 'string', maxLength: 100 },
        manufacturer: { type: 'string', maxLength: 100 },
        condition: { type: 'string', enum: ['NEW', 'EXCELLENT', 'GOOD', 'FAIR', 'POOR'] },
        vehicleCompatibility: { type: 'string', maxLength: 500 },
        installationDate: { type: 'string', maxLength: 50 },
        warrantyStatus: { type: 'string', enum: ['ACTIVE', 'EXPIRED', 'VOIDED'] },
        mileageAtInstall: { type: 'integer', min: 0 },
        value: { type: 'number', min: 0 },
        weight: { type: 'number', min: 0 },
        specifications: { type: 'string', maxLength: 1000 }
      }
    },
    tradingUnit: 'part',
    marketSymbol: 'VENGN'
  },

  VEHICLE_AERO_PARTS: {
    id: 'vehicleAeroParts',
    name: 'Vehicle Aerodynamic Components',
    category: 'automotive',
    classType: 'fungibleCommodity',
    composability: {
      enabled: true,
      standard: 'CIP-227',
      partCategory: 'aerodynamics',
      attachable: true,
      detachable: true,
      critical: false
    },
    standardSchema: {
      required: ['partName', 'partNumber', 'manufacturer', 'material', 'downforceRating'],
      specifications: {
        partName: { type: 'string', maxLength: 200 },
        partNumber: { type: 'string', maxLength: 100 },
        manufacturer: { type: 'string', maxLength: 100 },
        material: { type: 'string', enum: ['Carbon Fiber', 'Aluminum', 'Fiberglass', 'ABS Plastic'] },
        downforceRating: { type: 'number', min: 0 },
        dragCoefficient: { type: 'number', min: 0 },
        finish: { type: 'string', enum: ['Raw Carbon', 'Clear Coat', 'Painted', 'Wrapped'] },
        installationComplexity: { type: 'string', enum: ['SIMPLE', 'MODERATE', 'COMPLEX', 'PROFESSIONAL_REQUIRED'] },
        value: { type: 'number', min: 0 },
        weight: { type: 'number', min: 0 }
      }
    },
    tradingUnit: 'part',
    marketSymbol: 'VAERO'
  },

  HERITAGE_VEHICLES: {
    id: 'heritageVehicles',
    name: 'Classic & Heritage Vehicles',
    category: 'automotive',
    classType: 'uniqueCollectible',
    composability: {
      enabled: true,
      standard: 'CIP-227',
      supportedParts: ['engine', 'interior', 'bodywork'],
      restrictedTrading: true
    },
    standardSchema: {
      required: ['vehicleName', 'manufacturer', 'year', 'heritageSignificance', 'provenance', 'authenticityScore'],
      specifications: {
        vehicleName: { type: 'string', maxLength: 200 },
        manufacturer: { type: 'string', maxLength: 100 },
        year: { type: 'integer', min: 1885, max: 1995 },
        heritageSignificance: { 
          type: 'string', 
          enum: ['RACING_HISTORY', 'CELEBRITY_OWNED', 'PROTOTYPE', 'FIRST_PRODUCTION', 'LAST_PRODUCTION', 'DESIGN_MILESTONE'] 
        },
        provenance: { type: 'string', maxLength: 2000 },
        authenticityScore: { type: 'integer', min: 0, max: 100 },
        originalParts: { type: 'integer', min: 0, max: 100 },
        restorationHistory: { type: 'string', maxLength: 2000 },
        awards: { type: 'string', maxLength: 1000 },
        marketValue: { type: 'number', min: 0 },
        insuranceValue: { type: 'number', min: 0 },
        rarity: { type: 'string', enum: ['UNIQUE', 'EXTREMELY_RARE', 'RARE', 'LIMITED', 'UNCOMMON'] }
      }
    },
    tradingUnit: 'vehicle',
    marketSymbol: 'HERITAGE'
  },

  COMPANY_STOCK_SHARES: {
    id: 'companyStockShares',
    name: 'Company Stock Shares',
    category: 'stockShares',
    classType: 'fungibleCommodity',
    standardSchema: {
      required: ['companyName', 'shareClass', 'jurisdiction', 'registrationNumber', 'currency', 'totalAuthorizedShares'],
      specifications: {
        companyName: { type: 'string', maxLength: 200 },
        shareClass: { type: 'string', enum: ['Ordinary', 'Preferred', 'Class A', 'Class B'] },
        jurisdiction: { type: 'string', maxLength: 100 },
        registrationNumber: { type: 'string', maxLength: 64 },
        currency: { type: 'string', enum: ['USD', 'EUR', 'CHF', 'GBP'] },
        parValue: { type: 'number', min: 0 },
        totalAuthorizedShares: { type: 'integer', min: 1 },
        isin: { type: 'string', pattern: '^[A-Z]{2}[A-Z0-9]{9}[0-9]$' }
      }
    },
    tradingUnit: 'share',
    marketSymbol: 'EQTY'
  }
};

const buildInitialTemplatesRecord = () =>
  Object.values(ASSET_CLASS_TEMPLATES).reduce((acc, t) => {
    acc[t.id] = t;
    return acc;
  }, {});

// Initial state
const initialState = {
  assetClasses: [],
  assetClassTemplates: buildInitialTemplatesRecord(),
  batchHistory: {},
  marketPairs: {},
  loading: false,
  error: null,
  statistics: {
    totalClasses: 0,
    totalBatches: 0,
    activeMarkets: 0
  }
};

// Action types
const ASSET_CLASS_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ASSET_CLASSES: 'SET_ASSET_CLASSES',
  CREATE_ASSET_CLASS: 'CREATE_ASSET_CLASS',
  UPDATE_ASSET_CLASS: 'UPDATE_ASSET_CLASS',
  ADD_BATCH: 'ADD_BATCH',
  CREATE_MARKET_PAIR: 'CREATE_MARKET_PAIR',
  UPDATE_STATISTICS: 'UPDATE_STATISTICS',
  VALIDATE_SCHEMA: 'VALIDATE_SCHEMA',
  SET_ASSET_CLASS_TEMPLATES: 'SET_ASSET_CLASS_TEMPLATES'
};

// Reducer
const assetClassReducer = (state, action) => {
  switch (action.type) {
    case ASSET_CLASS_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ASSET_CLASS_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ASSET_CLASS_ACTIONS.SET_ASSET_CLASSES:
      return {
        ...state,
        assetClasses: action.payload,
        loading: false,
        error: null
      };
    
    case ASSET_CLASS_ACTIONS.CREATE_ASSET_CLASS:
      return {
        ...state,
        assetClasses: [...state.assetClasses, action.payload],
        loading: false
      };
    
    case ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS:
      return {
        ...state,
        assetClasses: state.assetClasses.map(assetClass =>
          assetClass.id === action.payload.id ? { ...assetClass, ...action.payload } : assetClass
        )
      };
    
    case ASSET_CLASS_ACTIONS.ADD_BATCH:
      return {
        ...state,
        batchHistory: {
          ...state.batchHistory,
          [action.payload.assetClassId]: [
            ...(state.batchHistory[action.payload.assetClassId] || []),
            action.payload.batch
          ]
        }
      };
    
    case ASSET_CLASS_ACTIONS.CREATE_MARKET_PAIR:
      return {
        ...state,
        marketPairs: {
          ...state.marketPairs,
          [action.payload.symbol]: action.payload
        }
      };
    
    case ASSET_CLASS_ACTIONS.UPDATE_STATISTICS:
      return {
        ...state,
        statistics: { ...state.statistics, ...action.payload }
      };

    case ASSET_CLASS_ACTIONS.SET_ASSET_CLASS_TEMPLATES:
      return {
        ...state,
        assetClassTemplates: action.payload
      };

    default:
      return state;
  }
};

// Provider component
export const AssetClassProvider = ({ children }) => {
  const [state, dispatch] = useReducer(assetClassReducer, initialState);
  const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  /**
   * Validates metadata against asset class standardSchema (required + specifications).
   * Form inputs are often strings; number/integer specs are coerced before type checks.
   * @returns {{ messages: string[], fieldErrors: Record<string, string> }}
   */
  const validateMetadataSchema = (metadata, schema) => {
    const messages = [];
    const fieldErrors = {};

    const add = (field, msg) => {
      messages.push(msg);
      if (field && !fieldErrors[field]) {
        fieldErrors[field] = msg;
      }
    };

    if (!schema || !Array.isArray(schema.required)) {
      return { messages: [], fieldErrors: {} };
    }

    const specs = schema.specifications || {};

    const isBlank = (v) =>
      v === undefined ||
      v === null ||
      (typeof v === 'string' && v.trim() === '');

    const coerceForSpec = (field, raw) => {
      const spec = specs[field];
      if (!spec) return raw;
      if (isBlank(raw)) return undefined;
      if (spec.type === 'number') {
        if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
        const n = parseFloat(String(raw).trim().replace(/,/g, ''));
        return Number.isNaN(n) ? raw : n;
      }
      if (spec.type === 'integer') {
        if (typeof raw === 'number' && Number.isInteger(raw)) return raw;
        const t = String(raw).trim();
        const n = Number(t);
        if (Number.isNaN(n)) return raw;
        if (!Number.isInteger(n)) return raw;
        return n;
      }
      return raw;
    };

    for (const field of schema.required) {
      if (isBlank(metadata[field])) {
        add(field, `Required field '${field}' is missing`);
      }
    }

    for (const [field, spec] of Object.entries(specs)) {
      const raw = metadata[field];
      if (isBlank(raw)) continue;

      const value = coerceForSpec(field, raw);

      switch (spec.type) {
        case 'number': {
          if (typeof value !== 'number' || Number.isNaN(value)) {
            add(field, `Field '${field}' must be a number`);
            break;
          }
          if (spec.min !== undefined && value < spec.min) {
            add(field, `Field '${field}' must be at least ${spec.min}`);
            break;
          }
          if (spec.max !== undefined && value > spec.max) {
            add(field, `Field '${field}' must be at most ${spec.max}`);
          }
          break;
        }
        case 'integer': {
          if (typeof value !== 'number' || Number.isNaN(value) || !Number.isInteger(value)) {
            add(field, `Field '${field}' must be an integer`);
            break;
          }
          if (spec.min !== undefined && value < spec.min) {
            add(field, `Field '${field}' must be at least ${spec.min}`);
            break;
          }
          if (spec.max !== undefined && value > spec.max) {
            add(field, `Field '${field}' must be at most ${spec.max}`);
          }
          break;
        }
        case 'string': {
          let str = typeof value === 'string' ? value : String(value);
          str = str.trim();
          if (spec.maxLength !== undefined && str.length > spec.maxLength) {
            add(field, `Field '${field}' must be at most ${spec.maxLength} characters`);
            break;
          }
          if (spec.enum && !spec.enum.includes(str)) {
            add(field, `Field '${field}' must be one of: ${spec.enum.join(', ')}`);
            break;
          }
          if (spec.pattern) {
            let candidate = str;
            const rawPattern = String(spec.pattern);
            if (rawPattern.includes('A-Z')) {
              candidate = str.toUpperCase();
            }
            // Legacy apartment template required 2+ chars; single-digit units (e.g. "1") are valid.
            let effectivePattern = rawPattern;
            let patternHint = spec.patternDescription;
            if (field === 'unitNumber' && rawPattern === '^[A-Z0-9]{2,10}$') {
              effectivePattern = '^[A-Z0-9]{1,10}$';
              patternHint =
                patternHint ||
                '1-10 characters: letters (A-Z) and/or digits (e.g. 1, 12, 4B). Lowercase letters are normalized.';
            }
            try {
              if (!new RegExp(effectivePattern).test(candidate)) {
                add(
                  field,
                  patternHint || `Field '${field}' does not match the required format`
                );
              }
            } catch {
              add(field, `Field '${field}' has an invalid validation rule`);
            }
          }
          break;
        }
        case 'date':
        case 'array':
        default:
          break;
      }
    }

    return { messages, fieldErrors };
  };

  const loadAssetClasses = useCallback(async () => {
    dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });

    try {
      const { data } = await assetClassesApi.list();
      const assetClasses = data.map(mapAssetClassToFrontend);
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ASSET_CLASSES, payload: assetClasses });
      return assetClasses;
    } catch (error) {
      const status = error.response?.status;
      const message =
        status === 401 || status === 403
          ? null
          : error.response?.data?.message || error.message;

      if (message) {
        dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      } else {
        dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
      }

      return [];
    }
  }, []);

  const getAssetClassById = async (assetClassId, refresh = false) => {
    const existingAssetClass = state.assetClasses.find((assetClass) => assetClass.id === assetClassId);
    if (existingAssetClass && !refresh) {
      return existingAssetClass;
    }

    const { data } = await assetClassesApi.getById(assetClassId);
    const assetClass = mapAssetClassToFrontend(data);
    dispatch({
      type: existingAssetClass
        ? ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS
        : ASSET_CLASS_ACTIONS.CREATE_ASSET_CLASS,
      payload: assetClass
    });

    return assetClass;
  };

  const prepareContractDeployment = async (assetClassId, payload) => {
    const { data } = await assetClassesApi.prepareDeployContract(assetClassId, payload);
    return normalizePreparedTransactionResponse(data) ?? data;
  };

  const submitContractDeployment = async (assetClassId, payload) => {
    const { data } = await assetClassesApi.deployContract(assetClassId, payload);
    try {
      await getAssetClassById(assetClassId, true);
    } catch {
      /* list/detail refresh is best-effort; callers may poll */
    }
    return data;
  };

  const prepareCustodyVaultDeployment = async (assetClassId, authorizedTokenizerId, payload) => {
    const { data } = await assetClassesApi.prepareDeployCustodyVaultForTokenizer(
      assetClassId,
      authorizedTokenizerId,
      payload
    );
    return normalizePreparedTransactionResponse(data) ?? data;
  };

  const submitCustodyVaultDeployment = async (assetClassId, authorizedTokenizerId, payload) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });
      const { data } = await assetClassesApi.deployCustodyVaultForTokenizer(
        assetClassId,
        authorizedTokenizerId,
        payload
      );
      const { assetClass, deploymentTransactionHash } = mapTokenizerContractDeployResponse(data);
      dispatch({ type: ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS, payload: assetClass });
      toast.success('Custody vault contract deployed and saved');
      return { assetClass, deploymentTransactionHash };
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      throw error;
    } finally {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Create Asset Class
  const createAssetClass = async (classData, options = {}) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });
      const { data } = await assetClassesApi.create(
        toAssetClassRequest(classData, { isPlatformStandard: options.platformStandard === true })
      );
      const assetClass = mapAssetClassToFrontend(data);

      dispatch({ type: ASSET_CLASS_ACTIONS.CREATE_ASSET_CLASS, payload: assetClass });

      toast.success(`Asset class "${assetClass.name}" created successfully!`);
      return assetClass;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      toast.error(message || 'Failed to create asset class');
      throw error;
    }
  };

  const updateAssetClass = async (assetClassId, updates) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });

      const existingAssetClass = state.assetClasses.find((assetClass) => assetClass.id === assetClassId);
      if (!existingAssetClass) {
        throw new Error('Asset class not found');
      }

      let response;
      if (updates.status && updates.status !== existingAssetClass.status) {
        response = await assetClassesApi.suspend(assetClassId);
      } else {
        response = await assetClassesApi.update(
          assetClassId,
          toAssetClassUpdateRequest(existingAssetClass, updates)
        );
      }

      const assetClass = mapAssetClassToFrontend(response.data);
      dispatch({ type: ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS, payload: assetClass });
      toast.success(`Asset class "${assetClass.name}" updated`);
      return assetClass;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      toast.error(message || 'Failed to update asset class');
      throw error;
    } finally {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const suspendAssetClass = async (assetClassId) => updateAssetClass(assetClassId, { status: 'Inactive' });

  const setAssetClassCustodyVault = async (assetClassId, authorizedTokenizerId, payload) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });
      const { data } = await assetClassesApi.setCustodyVaultForTokenizer(
        assetClassId,
        authorizedTokenizerId,
        payload
      );
      const assetClass = mapAssetClassToFrontend(data);
      dispatch({ type: ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS, payload: assetClass });
      toast.success('Custody vault settings saved');
      return assetClass;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      toast.error(message || 'Failed to save custody vault');
      throw error;
    } finally {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const prepareFractionTokenDeployment = async (assetClassId, authorizedTokenizerId, payload) => {
    const { data } = await assetClassesApi.prepareDeployFractionTokenForTokenizer(
      assetClassId,
      authorizedTokenizerId,
      payload
    );
    return normalizePreparedTransactionResponse(data) ?? data;
  };

  const submitFractionTokenDeployment = async (assetClassId, authorizedTokenizerId, payload) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });
      const { data } = await assetClassesApi.deployFractionTokenForTokenizer(
        assetClassId,
        authorizedTokenizerId,
        payload
      );
      const { assetClass, deploymentTransactionHash } = mapTokenizerContractDeployResponse(data);
      dispatch({ type: ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS, payload: assetClass });
      toast.success('Fraction ERC-20 (shares) contract deployed and saved');
      return { assetClass, deploymentTransactionHash };
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      throw error;
    } finally {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const setAssetClassFractionToken = async (assetClassId, authorizedTokenizerId, payload) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });
      const { data } = await assetClassesApi.setFractionTokenForTokenizer(
        assetClassId,
        authorizedTokenizerId,
        payload
      );
      const assetClass = mapAssetClassToFrontend(data);
      dispatch({ type: ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS, payload: assetClass });
      toast.success('Fraction token settings saved');
      return assetClass;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: message });
      toast.error(message || 'Failed to save fraction token');
      throw error;
    } finally {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Add new batch to existing asset class
  const addBatchToAssetClass = async (assetClassId, batchData) => {
    try {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: true });
      
      const assetClass = state.assetClasses.find(ac => ac.id === assetClassId);
      if (!assetClass) throw new Error('Asset class not found');
      
      // Validate metadata against schema
      const { messages: validationMessages } = validateMetadataSchema(
        batchData.metadata,
        assetClass.standardSchema
      );
      if (validationMessages.length > 0) {
        throw new Error(`Schema validation failed: ${validationMessages.join(', ')}`);
      }
      
      const batch = {
        id: generateId(),
        batchNumber: assetClass.totalBatches + 1,
        assetClassId,
        ...batchData,
        createdAt: new Date().toISOString(),
        status: 'verified'
      };
      
      dispatch({ type: ASSET_CLASS_ACTIONS.ADD_BATCH, payload: { assetClassId, batch } });
      
      // Update asset class statistics
      dispatch({
        type: ASSET_CLASS_ACTIONS.UPDATE_ASSET_CLASS,
        payload: {
          id: assetClassId,
          totalBatches: assetClass.totalBatches + 1,
          totalSupply: assetClass.totalSupply + (batchData.quantity || 1),
          batchIds: [...assetClass.batchIds, batch.id],
          lastBatchDate: batch.createdAt
        }
      });
      
      toast.success(`Batch ${batch.batchNumber} added to ${assetClass.name}!`);
      
      return batch;
    } catch (error) {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_ERROR, payload: error.message });
      toast.error(`Failed to add batch: ${error.message}`);
      throw error;
    } finally {
      dispatch({ type: ASSET_CLASS_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const resolveTemplate = (templateId) => {
    if (!templateId) {
      return null;
    }

    const fromState = state.assetClassTemplates[templateId];
    if (fromState) {
      return fromState;
    }

    return Object.values(state.assetClassTemplates).find((t) => t.id === templateId) || null;
  };

  const createFromTemplate = async (templateId, customizations = {}, options = {}) => {
    const template = resolveTemplate(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    const { composability: _c, isTemplate: _i, ...rest } = template;
    const classData = {
      ...rest,
      ...customizations,
      name: customizations.name || template.name
    };

    return await createAssetClass(classData, { platformStandard: options.platformStandard === true });
  };

  // Get batches for asset class
  const getBatchesForAssetClass = (assetClassId) => {
    return state.batchHistory[assetClassId] || [];
  };

  // Get market pair for asset class
  const getMarketPairForAssetClass = (assetClassId) => {
    return Object.values(state.marketPairs).find(pair => pair.assetClassId === assetClassId);
  };

  // Update statistics
  useEffect(() => {
    const totalClasses = state.assetClasses.length;
    const totalBatches = Object.values(state.batchHistory).reduce((sum, batches) => sum + batches.length, 0);
    const activeMarkets = Object.values(state.marketPairs).filter(pair => pair.isActive).length;
    
    dispatch({
      type: ASSET_CLASS_ACTIONS.UPDATE_STATISTICS,
      payload: {
        totalClasses,
        totalBatches,
        activeMarkets
      }
    });
  }, [state.assetClasses, state.batchHistory, state.marketPairs]);

  const loadAssetClassTemplates = useCallback(async () => {
    try {
      const { data } = await assetClassTemplatesApi.list();
      const mapped = (data || []).map(mapAssetClassTemplateDtoToFrontend).filter(Boolean);
      const record = {};
      for (const t of mapped) {
        record[t.id] = t;
      }
      if (Object.keys(record).length > 0) {
        dispatch({ type: ASSET_CLASS_ACTIONS.SET_ASSET_CLASS_TEMPLATES, payload: record });
      }
    } catch {
      /* keep built-in initial templates when API unavailable */
    }
  }, []);

  useEffect(() => {
    loadAssetClasses();
    loadAssetClassTemplates();
  }, [loadAssetClasses, loadAssetClassTemplates]);

  const value = {
    ...state,
    loadAssetClasses,
    loadAssetClassTemplates,
    getAssetClassById,
    prepareContractDeployment,
    submitContractDeployment,
    prepareCustodyVaultDeployment,
    submitCustodyVaultDeployment,
    prepareFractionTokenDeployment,
    submitFractionTokenDeployment,
    createAssetClass,
    updateAssetClass,
    suspendAssetClass,
    setAssetClassCustodyVault,
    setAssetClassFractionToken,
    addBatchToAssetClass,
    createFromTemplate,
    getBatchesForAssetClass,
    getMarketPairForAssetClass,
    validateMetadataSchema,
    ASSET_CLASS_TYPES,
    ASSET_CLASS_TEMPLATES
  };

  return (
    <AssetClassContext.Provider value={value}>
      {children}
    </AssetClassContext.Provider>
  );
};

export const useAssetClass = () => {
  const context = useContext(AssetClassContext);
  if (!context) {
    throw new Error('useAssetClass must be used within an AssetClassProvider');
  }
  return context;
}; 